import React, { useEffect, useLayoutEffect } from 'react'

import { FadeIn, Modal, ChallengesBox } from '@pb/components'
import { getLabelFromStorage, useMediaQuery, Breakpoints, getLocale } from '@pb/utils'
import { useDecision } from '@optimizely/react-sdk'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { ACCOUNT_ROUTES } from 'App/constants'
import { operationSetIsChallengesBannerTrackingSent } from 'App/duck/operations'
import appSelectors from 'App/duck/selectors'
import optimizelyConstants from 'constants/optimizely'
import { challengesPage } from 'features/account/constants'
import getAccountTracking from 'features/account/helpers/getAccountTracking'
import { PAGE_IDS } from 'features/account/helpers/getAccountTrackingData'
import useTrackAccountPageView from 'features/account/hooks/useTrackAccountPageView'
import { operationFetchOrderOfPoints } from 'features/account/points-history-legacy/duck/operations'
import pointsSelector from 'features/account/points-history-legacy/duck/selectors'
import { operationFetchOnlineActiveOrders } from 'features/account/purchases/duck/operations'
import purchasesSelectors from 'features/account/purchases/duck/selectors'
import { trackOptimizelyEvent } from 'helpers/optimizely'

import styles from './ClubCard.scss'
import CardBack from './components/CardBack'
import CardBackTitle from './components/CardBackTitle'
import CardFront from './components/CardFront'
import ClubCardLoader from './components/ClubCardLoader'
import IconLinkList from './components/IconLinkList/IconLinkList'
import { operationToggleClubCard } from './duck/operations'
import cardSelectors from './duck/selectors'

const ClubCard: React.FC = () => {
  const appState = useSelector((state) => state)
  const isUserLoading = useSelector(appSelectors.getIsUserLoading)
  const shouldShowClubCard = useSelector(cardSelectors.getShouldShowClubCard)
  const isClubCardDrawerOpen = useSelector(cardSelectors.getIsClubCardDrawerOpen)
  const name = useSelector(appSelectors.getUserDisplayName)
  const firstName = useSelector(appSelectors.getUserFirstName)
  const email = useSelector(appSelectors.getUserEmail)
  const currentBalance = useSelector(cardSelectors.getCurrentBalance) || 0
  const isOrderOfPointsLoaded = useSelector(pointsSelector.getIsOrderOfPointsLoaded)

  const dispatch = useDispatch()
  const actionToggleClubCard = (): void => {
    dispatch(operationToggleClubCard())
  }
  const fetchOrderOfPoints = (): void => {
    dispatch(operationFetchOrderOfPoints())
  }
  const history = useHistory()

  const shouldShowQuickAccessButtons = useMediaQuery(Breakpoints.LARGE)
  const isChallengesBannerTrackingSent = useSelector(appSelectors.getIsChallengesBannerTrackingSent)
  const shouldFetchOnlineActiveOrders = useSelector(
    purchasesSelectors.getShouldFetchOnlineActiveOrders,
  )

  const [{ enabled: isEntryPointBannerActive }] = useDecision(
    optimizelyConstants.GAMIFICATION_CHALLENGES,
  )
  const [{ variables }] = useDecision(optimizelyConstants.GAMIFICATION_CHALLENGES)

  const variationLocales = variables && (variables.entry_point_banner as Object)

  const [{ variationKey }] = useDecision(optimizelyConstants.GAMIFICATION_CHALLENGES)
  const { trackViewEvent } = getAccountTracking(appState)
  const trackPageView = useTrackAccountPageView()

  useLayoutEffect(() => {
    fetchOrderOfPoints()
  }, [])

  useEffect(() => {
    if (isEntryPointBannerActive && !isChallengesBannerTrackingSent) {
      trackOptimizelyEvent(optimizelyConstants.VIEW_ENTRY_POINT_BANNER)
    }
    trackPageView(PAGE_IDS.account)
    trackViewEvent(challengesPage(variationKey || ''))
    dispatch(operationSetIsChallengesBannerTrackingSent(true))
  }, [])

  useEffect(() => {
    if (shouldFetchOnlineActiveOrders) {
      dispatch(operationFetchOnlineActiveOrders({ start: 0, end: 4, active: true }))
    }
  }, [shouldFetchOnlineActiveOrders])

  const challengesBoxclick = (): void => {
    if (isEntryPointBannerActive) {
      trackOptimizelyEvent(optimizelyConstants.CLICK_ENTRY_POINT_BANNER)
    }
    history.push(ACCOUNT_ROUTES.ChallengesPage.path)
  }

  const getLocalGamificationEntryPointText = (text): string => {
    const locale = getLocale()

    if (Object.prototype.hasOwnProperty.call(variationLocales ?? {}, locale)) {
      return text === 'title'
        ? variationLocales[locale].banner_title
        : text === 'description'
        ? variationLocales[locale].banner_description_1
        : variationLocales[locale].banner_description_2
    }

    return ''
  }

  if (isUserLoading || !isOrderOfPointsLoaded) {
    return <ClubCardLoader />
  }
  if (shouldShowClubCard) {
    return (
      <FadeIn duration={500} className={styles.container}>
        <Modal
          closeButtonText={getLabelFromStorage('general.close')}
          isOpen={isClubCardDrawerOpen}
          toggleModal={actionToggleClubCard}
          className={styles.sideDrawer}
          modalType="stickyLeft"
          isWithoutBackgroundColor
          customTitle={<CardBackTitle name={name} email={email} />}
        >
          <CardBack currentBalance={currentBalance} />
        </Modal>
        <CardFront
          name={firstName}
          email={email}
          currentBalance={currentBalance}
          actionToggleClubCard={actionToggleClubCard}
          isEntryPointBannerActive={isEntryPointBannerActive}
        />
        {isEntryPointBannerActive && (
          <ChallengesBox
            title={getLocalGamificationEntryPointText('title')}
            description={getLocalGamificationEntryPointText('description')}
            subtitle={getLocalGamificationEntryPointText('subtitle')}
            onClick={() => challengesBoxclick()}
          />
        )}
        {shouldShowQuickAccessButtons && <IconLinkList />}
      </FadeIn>
    )
  }
  return null
}

export default ClubCard
